import pathRoutes from "../../../routes/pathRoutes";
import { Remove } from "../../../components/Actions/delete";
import { Edit } from "../../../components/Actions/edit";
import { GridColDef } from "@mui/x-data-grid";
import { View } from "../../../components/Actions/view";
import { NavigateFunction } from "react-router-dom";
import { useEfficiencies } from "@services/servicesGenericsTables";
import { EFFICIENCY_TYPE } from "./constants";
import { Box } from "@mui/material";
import { useCalibrationTritium } from "./useCalibration";
import { PermissionComponentGate } from "@app/utils/PermissionComponentGate";
import { RolesTypes } from "@app/pages/users/utils/helpers";

type ColumnsProps = (params: any) => void;

export const columnsEfficiency = (
  handleDelete: ColumnsProps,
  navigate: NavigateFunction
): GridColDef[] => [
  {
    field: "id",
    headerName: "Id. Cal. Eficiencias",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => {
      return `CE${params.value}`;
    },
  },
  {
    field: "date",
    headerName: "Fecha alta",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => {
      const date = new Date(params.value);
      return date.toLocaleDateString();
    },
  },
  {
    field: "equipment",
    headerName: "Equipo",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => params.value,
    valueGetter: (params) => {
      return params.row.equipment?.codigo
        ? `${params.row.equipment?.codigo}`
        : "";
    },
  },
  {
    field: "efficiency_type",
    headerName: "Tipo Eficiencia",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => {
      return (
        <Box
          component="span"
          sx={{
            wordBreak: "break-word",
            whiteSpace: "normal",
            display: "block",
            textAlign: "center",
          }}
        >
          {EFFICIENCY_TYPE[params.value]}
        </Box>
      );
    },
  },
  {
    field: "analysis",
    headerName: "Análisis",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => (
      <Box
        component="span"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "normal",
          display: "block",
          textAlign: "center",
        }}
      >
        {params.value}
      </Box>
    ),
    valueGetter: (params) => {
      if (!params.row.analysis) return "";
      
      return params.row.analysis
        .map((analysis, index) => `${analysis?.descripcion || `Análisis ${index + 1}`}`)
        .join(", ");
    },
  },
  {
    field: "part",
    headerName: "Parte",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => (
      <Box
        component="span"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "normal",
          display: "block",
          textAlign: "center",
        }}
      >
        {params.value}
      </Box>
    ),
    valueGetter: (params) => {
      return params.row.part || "";
    },
  },
  {
    field: "actions",
    headerName: "Acciones",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => (
      <>
        <View
          to={`${pathRoutes.ADMIN_CALIBRACIONES}/ver/eficiencia/${params.id}`}
        />
        <PermissionComponentGate
          permissions={[RolesTypes.ADMIN, RolesTypes.SUPER_ADMIN]}
        >
          <Edit
            to={`${pathRoutes.ADMIN_CALIBRACIONES}/editar/eficiencia/${params.id}`}
          />
          <Remove
            onRemove={() => useEfficiencies.getState().remove(params.row.id)}
          />
        </PermissionComponentGate>
      </>
    ),
  },
];

export const columnsAnalysis = (
  handleDelete: ColumnsProps,
  navigate: NavigateFunction
): GridColDef[] => [
  {
    field: "id",
    headerName: "Id. Cal. Análisis",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => {
      return `C${params.value}`;
    },
  },
  {
    field: "date",
    headerName: "Fecha alta",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => {
      const date = new Date(params.value);
      return date.toLocaleDateString();
    },
  },
  {
    field: "equipment",
    headerName: "Equipo",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => params.value,
    valueGetter: (params) => {
      return params.row.equipment?.codigo
        ? `${params.row.equipment?.codigo}`
        : "";
    },
  },
  {
    field: "analysis_type",
    headerName: "Tipo Análisis",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => {
      return (
        <Box
          component="span"
          sx={{
            wordBreak: "break-word",
            whiteSpace: "normal",
            display: "block",
            textAlign: "center",
          }}
        >
          {params.value}
        </Box>
      );
    },
    valueGetter: (params) => {
      if (!params.row.analysis_type) return "";
      
      // Manejar analysis_type como array o objeto individual
      if (Array.isArray(params.row.analysis_type)) {
        return params.row.analysis_type
          .map(type => type?.descripcion || "")
          .filter(desc => desc)
          .join(", ");
      } else {
        return params.row.analysis_type?.descripcion || "";
      }
    },
  },
  {
    field: "actions",
    headerName: "Acciones",
    headerAlign: "center",
    align: "center",
    flex: 1,
    renderCell: (params) => (
      <>
        <View
          to={`${pathRoutes.ADMIN_CALIBRACIONES}/ver/analisis/${params.id}`}
        />
        <PermissionComponentGate
          permissions={[RolesTypes.ADMIN, RolesTypes.SUPER_ADMIN]}
        >
          <Edit
            to={`${pathRoutes.ADMIN_CALIBRACIONES}/editar/analisis/${params.id}`}
          />
          <Remove
            onRemove={() =>
              useCalibrationTritium.getState().remove(params.row.id)
            }
          />
        </PermissionComponentGate>
      </>
    ),
  },
];

export const columnsAnalisisCurva: GridColDef[] = [
  {
    field: "sqp_tsie_quenching",
    headerName: "SQP/TSIE (Quenching)",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
  {
    field: "efficiency",
    headerName: "Eficiencia",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
  {
    field: "fa_exp",
    headerName: "Fa exp",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
  {
    field: "fc_calc",
    headerName: "Fa calc",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
];

export const columnsAnalisisCurvaInputs: GridColDef[] = [
  {
    field: "sqp_tsie_quenching",
    headerName: "SQP/TSIE (Quenching)",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
  {
    field: "efficiency",
    headerName: "Eficiencia",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
];

export const columnsAnalisisCurvaAlfaInputs: GridColDef[] = [
  {
    field: "sqp_tsie_quenching", //TODO: Cambiar por el campo correcto
    headerName: "mg Torta",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
  {
    field: "efficiency",
    headerName: "Eficiencia",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
];

export const columnsAnalisisAlfaTotalCurva: GridColDef[] = [
  {
    field: "sqp_tsie_quenching", //TODO: Cambiar por el campo correcto
    headerName: "mg Torta",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
  {
    field: "efficiency",
    headerName: "Eficiencia",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
  {
    field: "fa_exp",
    headerName: "Fa exp",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
  {
    field: "fc_calc",
    headerName: "Fa calc",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
];

export const columnsAnalisisCurvaResult: GridColDef[] = [
  {
    field: "fa_exp",
    headerName: "Fa exp",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
  {
    field: "fc_calc",
    headerName: "Fa calc",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
];

export const columnsAnalisisCurvaResultAlfa: GridColDef[] = [
  {
    field: "fa_exp",
    headerName: "Fa exp",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
  {
    field: "fc_calc",
    headerName: "Fa calc",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
];

export const columnsAnalisisAlfaTotalCurvaInput: GridColDef[] = [
  {
    field: "sqp_tsie_quenching", //TODO: Cambiar por el campo correcto
    headerName: "mg Torta",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
  {
    field: "efficiency",
    headerName: "Eficiencia",
    headerAlign: "center",
    align: "center",
    editable: true,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber : params.value}
        </div>
      );
    },
  },
];

export const columnsAnalisisAlfaTotalCurvaResult: GridColDef[] = [
  {
    field: "fa_exp",
    headerName: "Fa exp",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
  {
    field: "fc_calc",
    headerName: "Fa calc",
    headerAlign: "center",
    align: "center",
    editable: false,
    flex: 1,
    renderCell: (params) => {
      const valueAsNumber = Number(params.value);
      const isValidNumber =
        !isNaN(valueAsNumber) &&
        !isNaN(parseFloat(params.value)) &&
        typeof valueAsNumber === "number";

      return (
        <div style={{ color: isValidNumber ? "inherit" : "red" }}>
          {isValidNumber ? valueAsNumber.toFixed(8) : params.value}
        </div>
      );
    },
  },
];
